import { forwardRef, useImperativeHandle, useLayoutEffect, useRef, useState, type ComponentProps, type FocusEvent, type Ref } from 'react'
import { cn } from '@/lib/cn'
import { trimDecimal } from '@/lib/format'
import { caretAfter, formatPhone, groupNumber, parseNumber, parsePhone } from '@/lib/mask'
import { Input } from './Input'

type BaseProps = Omit<ComponentProps<typeof Input>, 'value' | 'onChange' | 'type' | 'inputMode'>

const isNumberChar = (char: string) => /[\d.-]/.test(char)
const isDigit = (char: string) => /\d/.test(char)

/** Re-places the caret after a re-format, counting significant characters instead of positions. */
function useCaret(forwarded: Ref<HTMLInputElement>, significant: (char: string) => boolean) {
  const own = useRef<HTMLInputElement>(null)
  const pending = useRef<number | null>(null)
  useImperativeHandle(forwarded, () => own.current as HTMLInputElement)
  useLayoutEffect(() => {
    const input = own.current
    if (pending.current === null || !input || document.activeElement !== input) return
    const position = caretAfter(input.value, pending.current, significant)
    pending.current = null
    input.setSelectionRange(position, position)
  })
  const remember = (input: HTMLInputElement) => {
    const caret = input.selectionStart ?? input.value.length
    pending.current = [...input.value.slice(0, caret)].filter(significant).length
  }
  return { ref: own, remember }
}

type NumberInputProps = BaseProps & {
  value: string | number | null | undefined
  onValueChange?: (value: string) => void
  /** Fraction digits allowed: 0 for a count, unlimited when omitted. */
  decimals?: number
  allowNegative?: boolean
}

/**
 * A count or an amount: grouped thousands while typing ("1 234 567.5"), a
 * comma read as the point, the plain decimal text ("1234567.5") handed on.
 * Trailing zeros after the point are not shown ("90000.0000" → "90 000",
 * "12.50" → "12.5"), except while the user is typing them: what was just typed
 * stays as typed ("1.0" on the way to "1.05") until the field is left. Only
 * the display is trimmed; the value is handed on unchanged.
 */
export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(function NumberInput(
  { value, onValueChange, decimals, allowNegative, className, readOnly, disabled, onFocus, onBlur, ...props },
  ref,
) {
  const { ref: own, remember } = useCaret(ref, isNumberChar)
  const [focused, setFocused] = useState(false)
  const [typed, setTyped] = useState<string | null>(null)
  const plain = value === null || value === undefined ? '' : String(value)
  const shown = groupNumber(focused && plain === typed ? plain : trimDecimal(plain))
  return (
    <Input
      {...props}
      ref={own}
      inputMode={decimals === 0 ? 'numeric' : 'decimal'}
      autoComplete="off"
      className={cn('num', className)}
      value={shown}
      readOnly={readOnly}
      disabled={disabled}
      onFocus={(event) => {
        setFocused(true)
        onFocus?.(event)
      }}
      onBlur={(event) => {
        setFocused(false)
        setTyped(null)
        onBlur?.(event)
      }}
      onChange={(event) => {
        remember(event.target)
        const next = parseNumber(event.target.value, decimals, allowNegative)
        setTyped(next)
        onValueChange?.(next)
      }}
    />
  )
})

type PhoneInputProps = BaseProps & {
  value: string | null | undefined
  onValueChange?: (value: string) => void
}

/**
 * A phone number: shown as "+998 90 123 45 67" (another country as "+digits"),
 * handed on as "+998901234567". Nine digits typed without the country
 * code become an Uzbekistan number when the field is left.
 */
export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(function PhoneInput(
  { value, onValueChange, className, placeholder = '+998 90 123 45 67', onBlur, ...props },
  ref,
) {
  const { ref: own, remember } = useCaret(ref, isDigit)
  return (
    <Input
      {...props}
      ref={own}
      type="tel"
      inputMode="tel"
      autoComplete={props.autoComplete ?? 'off'}
      placeholder={placeholder}
      className={cn('num', className)}
      value={formatPhone(value ?? '')}
      onChange={(event) => {
        remember(event.target)
        onValueChange?.(parsePhone(event.target.value))
      }}
      onBlur={(event: FocusEvent<HTMLInputElement>) => {
        const digits = (value ?? '').replace(/\D/g, '')
        if (digits.length === 9 && !digits.startsWith('998')) onValueChange?.(`+998${digits}`)
        onBlur?.(event)
      }}
    />
  )
})
